import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Star as StarIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import jointAccountService, {
  JointAccountSummary,
  JointAccountMember,
  JointAccountAlias,
} from '../../services/jointAccountService';

const CONFIGURABLE_TABS = [
  { key: 'messages', label: 'Messages (Inbox/Sent)' },
  { key: 'address-book', label: 'Address Book' },
  { key: 'category-settings', label: 'Category Settings' },
  { key: 'account-history', label: 'Account History' },
  { key: 'public-profiles', label: 'Public Profiles' },
];
const ALL_TAB_KEYS = CONFIGURABLE_TABS.map(t => t.key);

interface EditAliasState {
  alias: JointAccountAlias;
  displayName: string;
  newPassword: string;
  role: string;
  canSpend: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  canInviteMembers: boolean;
  canEditSettings: boolean;
  allowedTabs: string[];
}

const AdditionalMembersManager: React.FC = () => {
  const { credentialType, accountTypeCode, isBusinessAccount, user } = useAuth();

  const [businessAccount, setBusinessAccount] = useState<JointAccountSummary | null>(null);
  const [ownerMembers, setOwnerMembers] = useState<JointAccountMember[]>([]);
  const [agentAliases, setAgentAliases] = useState<JointAccountAlias[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoInitializing, setAutoInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add member dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newOriginalUsername, setNewOriginalUsername] = useState('');
  const [newAliasUsername, setNewAliasUsername] = useState('');
  const [newRole, setNewRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [newCanSpend, setNewCanSpend] = useState(true);
  const [newCanDeposit, setNewCanDeposit] = useState(false);
  const [newCanWithdraw, setNewCanWithdraw] = useState(false);
  const [newCanInviteMembers, setNewCanInviteMembers] = useState(false);
  const [newCanEditSettings, setNewCanEditSettings] = useState(false);
  const [newAllowedTabs, setNewAllowedTabs] = useState<string[]>(ALL_TAB_KEYS);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Edit alias dialog
  const [editState, setEditState] = useState<EditAliasState | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Business type: a non-consumer account type OR any credential with bitIsBusinessAccount=true
  const isBusinessType = isBusinessAccount || (accountTypeCode && accountTypeCode !== 'CONSUMER');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const accounts = await jointAccountService.getMyAccounts();
      const business = accounts?.find(
        a => a.accountType === 'BUSINESS' && (a.userRole === 'PRIMARY' || a.userRole === 'ADMIN')
      );

      if (!business && isBusinessType) {
        // Auto-create a BUSINESS joint account silently
        setAutoInitializing(true);
        try {
          await jointAccountService.createAccount({
            accountName: user?.username ?? 'My Business Account',
            accountType: 'BUSINESS',
            isFamilyAccount: false,
            description: '',
            requireParentalApproval: false,
          });
          // Reload after creation
          const accounts2 = await jointAccountService.getMyAccounts();
          const business2 = accounts2?.find(
            a => a.accountType === 'BUSINESS' && (a.userRole === 'PRIMARY' || a.userRole === 'ADMIN')
          );
          setBusinessAccount(business2 || null);
          if (business2) await loadMembers(business2.jointAccountId);
        } finally {
          setAutoInitializing(false);
        }
        return;
      }

      setBusinessAccount(business || null);
      if (business) await loadMembers(business.jointAccountId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load business account');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async (accountId: string) => {
    const [members, aliases] = await Promise.all([
      jointAccountService.getMembers(accountId),
      jointAccountService.getAliases(accountId),
    ]);
    // Only show members who are NOT backed by a joint-account alias. Members that have
    // an aliasId are already displayed in the agentAliases list (as their alias username
    // "QixitMarketing-Beta01"). Showing them in both lists causes the duplicate row.
    setOwnerMembers((members || []).filter(m => !m.aliasId));
    setAgentAliases((aliases || []).filter(a => !a.isMinor));
  };

  // When originalUsername changes in the Add dialog, auto-fill alias
  const handleOriginalUsernameChange = (value: string) => {
    setNewOriginalUsername(value);
    const accountName = businessAccount?.accountName ?? user?.username ?? '';
    setNewAliasUsername(value.trim() ? `${accountName}-${value.trim()}` : '');
  };

  const handleAddMember = async () => {
    if (!newOriginalUsername.trim()) {
      setAddError('Please enter the username to invite');
      return;
    }
    if (!newAliasUsername.trim()) {
      setAddError('Alias username is required');
      return;
    }
    if (!businessAccount) return;

    try {
      setAdding(true);
      setAddError(null);

      const result = await jointAccountService.inviteWithAlias(businessAccount.jointAccountId, {
        originalUsername: newOriginalUsername.trim(),
        newAliasUsername: newAliasUsername.trim(),
        role: newRole,
        canSpend: newCanSpend,
        canDeposit: newCanDeposit,
        canWithdraw: newCanWithdraw,
        canInviteMembers: newCanInviteMembers,
        canEditSettings: newCanEditSettings,
        allowedTabs: newAllowedTabs.length === ALL_TAB_KEYS.length ? undefined : newAllowedTabs,
      });

      if (result.success) {
        setAddDialogOpen(false);
        resetAddDialog();
        await loadMembers(businessAccount.jointAccountId);
      } else {
        setAddError(result.message || 'Failed to add member');
      }
    } catch (err: any) {
      setAddError(err.response?.data?.message || err.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const resetAddDialog = () => {
    setNewOriginalUsername('');
    setNewAliasUsername('');
    setNewRole('MEMBER');
    setNewCanSpend(true);
    setNewCanDeposit(false);
    setNewCanWithdraw(false);
    setNewCanInviteMembers(false);
    setNewCanEditSettings(false);
    setNewAllowedTabs(ALL_TAB_KEYS);
    setAddError(null);
  };

  const handleOpenEditAlias = (alias: JointAccountAlias) => {
    setEditState({
      alias,
      displayName: alias.displayName ?? '',
      newPassword: '',
      role: alias.role ?? 'MEMBER',
      canSpend: alias.canSpend,
      canDeposit: alias.canDeposit,
      canWithdraw: alias.canWithdraw,
      canInviteMembers: alias.canInviteMembers,
      canEditSettings: alias.canEditSettings,
      allowedTabs: alias.allowedTabs ?? ALL_TAB_KEYS,
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editState || !businessAccount) return;
    try {
      setSaving(true);
      setEditError(null);

      await jointAccountService.updateAlias(editState.alias.aliasId, {
        displayName: editState.displayName || undefined,
        newPassword: editState.newPassword || undefined,
        allowedTabs: editState.allowedTabs.length === ALL_TAB_KEYS.length ? [] : editState.allowedTabs,
        role: editState.role,
        canSpend: editState.canSpend,
        canDeposit: editState.canDeposit,
        canWithdraw: editState.canWithdraw,
        canInviteMembers: editState.canInviteMembers,
        canEditSettings: editState.canEditSettings,
      });

      setEditDialogOpen(false);
      setEditState(null);
      await loadMembers(businessAccount.jointAccountId);
    } catch (err: any) {
      setEditError(err.response?.data?.message || err.message || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAlias = async (alias: JointAccountAlias) => {
    if (!businessAccount) return;
    if (!window.confirm(`Remove ${alias.aliasUsername} from this business account? They will lose access.`)) return;
    try {
      await jointAccountService.disableAlias(alias.aliasId);
      await loadMembers(businessAccount.jointAccountId);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const getRoleBadge = (role?: string) => {
    switch (role) {
      case 'PRIMARY':
        return <Chip icon={<StarIcon />} label="Primary Owner" size="small" color="primary" />;
      case 'ADMIN':
        return <Chip icon={<AdminIcon />} label="Owner" size="small" color="secondary" />;
      case 'MEMBER':
        return <Chip icon={<PersonIcon />} label="Agent" size="small" color="info" />;
      default:
        return <Chip label="Agent" size="small" />;
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  if (loading || autoInitializing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        {autoInitializing && (
          <Typography sx={{ ml: 2 }} color="text.secondary">
            Setting up business account…
          </Typography>
        )}
      </Box>
    );
  }

  if (!businessAccount) {
    // Not a business type — direct user to the external credentialing wizard
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <BusinessIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Business Account Required
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          You need a business account to add members. Complete the Credentialing Wizard to upgrade your account.
        </Typography>
      </Paper>
    );
  }

  const totalMembers = ownerMembers.length + agentAliases.length;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">{businessAccount.accountName}</Typography>
            <Chip label={credentialType || 'Business'} size="small" color="info" />
          </Box>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Account Members ({totalMembers})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { resetAddDialog(); setAddDialogOpen(true); }}
        >
          Add Member
        </Button>
      </Box>

      {totalMembers === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No members yet. Click "Add Member" to invite someone to this business account.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name / Login</TableCell>
                <TableCell>Invited As</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Owner rows — direct members (PRIMARY/ADMIN without alias) */}
              {ownerMembers.map(member => (
                <TableRow key={member.membershipId} sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell>
                    <Typography fontWeight="medium">{member.displayName || member.username}</Typography>
                    <Typography variant="caption" color="text.secondary">{member.username}</Typography>
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>{getRoleBadge(member.role)}</TableCell>
                  <TableCell>
                    <Chip label="Full Access" size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              ))}

              {/* Agent alias rows */}
              {agentAliases.map(alias => (
                <TableRow key={alias.aliasId}>
                  <TableCell>
                    <Typography fontWeight="medium">{alias.displayName || alias.aliasUsername}</Typography>
                    <Typography variant="caption" color="text.secondary">{alias.aliasUsername}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {alias.originalInvitedUsername}
                    </Typography>
                  </TableCell>
                  <TableCell>{getRoleBadge(alias.role)}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {alias.canSpend && <Chip label="Spend" size="small" variant="outlined" />}
                      {alias.canDeposit && <Chip label="Deposit" size="small" variant="outlined" />}
                      {alias.canWithdraw && <Chip label="Withdraw" size="small" variant="outlined" />}
                      {alias.canInviteMembers && <Chip label="Invite" size="small" variant="outlined" />}
                      {alias.canEditSettings && <Chip label="Settings" size="small" variant="outlined" />}
                      {alias.allowedTabs && alias.allowedTabs.length < ALL_TAB_KEYS.length && (
                        <Chip label="Limited Tabs" size="small" color="warning" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEditAlias(alias)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => handleRemoveAlias(alias)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Member Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Business Member</DialogTitle>
        <DialogContent>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the username of an existing user to invite. A new alias login will be created
            for this business account. An email will notify them of their new login details.
          </Alert>

          <TextField
            fullWidth
            label="Existing username to invite"
            value={newOriginalUsername}
            onChange={e => handleOriginalUsernameChange(e.target.value)}
            placeholder="e.g. Dave70"
            sx={{ mt: 1, mb: 2 }}
          />

          <TextField
            fullWidth
            label="Alias login username"
            value={newAliasUsername}
            onChange={e => setNewAliasUsername(e.target.value)}
            placeholder={`e.g. ${businessAccount?.accountName}-Dave70`}
            helperText="Auto-filled — you may edit it"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={newRole}
              label="Role"
              onChange={e => setNewRole(e.target.value as 'ADMIN' | 'MEMBER')}
            >
              <MenuItem value="ADMIN">Owner — full access, can manage members</MenuItem>
              <MenuItem value="MEMBER">Agent — limited access</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {[
              ['canSpend', 'Can Spend', newCanSpend, setNewCanSpend],
              ['canDeposit', 'Can Deposit', newCanDeposit, setNewCanDeposit],
              ['canWithdraw', 'Can Withdraw', newCanWithdraw, setNewCanWithdraw],
              ['canInvite', 'Can Invite Members', newCanInviteMembers, setNewCanInviteMembers],
              ['canSettings', 'Can Edit Settings', newCanEditSettings, setNewCanEditSettings],
            ].map(([key, label, val, setter]) => (
              <Grid item xs={6} key={key as string}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={val as boolean}
                      onChange={e => (setter as React.Dispatch<React.SetStateAction<boolean>>)(e.target.checked)}
                    />
                  }
                  label={label as string}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Tab Access</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Uncheck tabs to restrict which sections this member can access when logged in.
          </Typography>
          <Grid container>
            {CONFIGURABLE_TABS.map(tab => (
              <Grid item xs={6} key={tab.key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={newAllowedTabs.includes(tab.key)}
                      onChange={e => {
                        setNewAllowedTabs(prev =>
                          e.target.checked ? [...prev, tab.key] : prev.filter(k => k !== tab.key)
                        );
                      }}
                    />
                  }
                  label={tab.label}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddMember} disabled={adding}>
            {adding ? <CircularProgress size={24} /> : 'Invite & Create Alias'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Alias Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member — {editState?.alias.aliasUsername}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          {editState && (
            <>
              <TextField
                fullWidth
                label="Display Name"
                value={editState.displayName}
                onChange={e => setEditState(s => s && { ...s, displayName: e.target.value })}
                helperText={
                  editState.canWithdraw || editState.role === 'ADMIN' || editState.role === 'PRIMARY'
                    ? 'Must match government ID for Cash Redemption eligibility.'
                    : undefined
                }
                sx={{ mt: 1, mb: 2 }}
              />

              <TextField
                fullWidth
                label="New Password (leave blank to keep current)"
                type="password"
                value={editState.newPassword}
                onChange={e => setEditState(s => s && { ...s, newPassword: e.target.value })}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editState.role}
                  label="Role"
                  onChange={e => setEditState(s => s && { ...s, role: e.target.value })}
                >
                  <MenuItem value="ADMIN">Owner — full access, can manage members</MenuItem>
                  <MenuItem value="MEMBER">Agent — limited access</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  ['canSpend', 'Can Spend', editState.canSpend, (v: boolean) => setEditState(s => s && { ...s, canSpend: v })],
                  ['canDeposit', 'Can Deposit', editState.canDeposit, (v: boolean) => setEditState(s => s && { ...s, canDeposit: v })],
                  ['canWithdraw', 'Can Withdraw', editState.canWithdraw, (v: boolean) => setEditState(s => s && { ...s, canWithdraw: v })],
                  ['canInvite', 'Can Invite', editState.canInviteMembers, (v: boolean) => setEditState(s => s && { ...s, canInviteMembers: v })],
                  ['canSettings', 'Can Edit Settings', editState.canEditSettings, (v: boolean) => setEditState(s => s && { ...s, canEditSettings: v })],
                ].map(([key, label, val, setter]) => (
                  <Grid item xs={6} key={key as string}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={val as boolean}
                          onChange={e => (setter as (v: boolean) => void)(e.target.checked)}
                        />
                      }
                      label={label as string}
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Tab Access</Typography>
              <Grid container>
                {CONFIGURABLE_TABS.map(tab => (
                  <Grid item xs={6} key={tab.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={editState.allowedTabs.includes(tab.key)}
                          onChange={e => {
                            setEditState(s => {
                              if (!s) return s;
                              const tabs = e.target.checked
                                ? [...s.allowedTabs, tab.key]
                                : s.allowedTabs.filter(k => k !== tab.key);
                              return { ...s, allowedTabs: tabs };
                            });
                          }}
                        />
                      }
                      label={tab.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdditionalMembersManager;
